<!--
  ReportView.vue: renders a report model from GET /admin/enhanced-reports/:scope/:type/view.
  The same model is rendered server-side to PDF, so screen and PDF always match.
  Section kinds: meta, cards, callout, table, grid, notes, text, group (see backend htmlRenderer.js).
-->
<template>
  <div :dir="model.dir" class="report-view flex flex-col gap-5">
    <template v-for="(section, i) in model.sections" :key="i">
      <ReportSection :section="section" :dir="model.dir" :depth="0" />
    </template>
    <div v-if="model.footer || model.internalFooter" class="text-xs text-muted-foreground border-t border-border pt-3 space-y-1">
      <p v-if="model.internalFooter">{{ model.internalFooter }} <span class="italic opacity-80">({{ t('reportsExt.screenOnly') }})</span></p>
      <p v-if="model.footer">{{ model.footer }}</p>
    </div>
  </div>
</template>

<script setup>
import { defineComponent, h, ref, computed } from 'vue'
import { ChevronDown, Search } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
// Screen-only parts (formula/rate notes) are marked `internal` and left out of every export
const screenOnlyTag = () => h('span', { class: 'ms-1 italic opacity-70' }, `(${t('reportsExt.screenOnly')})`)

defineProps({ model: { type: Object, required: true } })

const TONE = {
  main: 'border-t-primary',
  ok: 'border-t-emerald-500',
  warn: 'border-t-amber-500',
  bad: 'border-t-red-500',
}
const PILL = {
  ok: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  bad: 'bg-red-50 text-red-700 ring-red-600/20',
  warn: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  blue: 'bg-sky-50 text-sky-700 ring-sky-600/20',
  '': 'bg-slate-100 text-slate-700 ring-slate-500/20',
}
const ROWS_COLLAPSED = 25

function renderCell(c, dir) {
  if (c === null || c === undefined || c === '') return h('span', { class: 'text-muted-foreground/60' }, '–')
  if (typeof c !== 'object') return String(c)
  if (c.pill !== undefined) {
    return h('span', { class: `inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset whitespace-nowrap ${PILL[c.pill] || PILL['']}` }, c.text)
  }
  if (c.note !== undefined || c.auto) {
    return h('div', { class: 'min-w-[12rem]' }, [
      c.text ? h('div', { dir: c.auto ? 'auto' : undefined, class: 'leading-snug' }, c.text) : null,
      c.note ? h('div', { dir, class: 'mt-0.5 text-[11px] leading-snug text-amber-700' }, c.note) : null,
    ])
  }
  if (c.muted) return h('span', { class: 'text-muted-foreground' }, c.text)
  if (c.strong) return h('span', { class: 'font-semibold' }, c.text)
  return String(c.text ?? '')
}

function cellSearchText(c) {
  if (c === null || c === undefined) return ''
  if (typeof c !== 'object') return String(c)
  return `${c.text ?? ''} ${c.note ?? ''}`
}

const ReportTable = defineComponent({
  props: { section: Object, dir: String, depth: Number },
  setup(props) {
    const query = ref('')
    const expanded = ref(false)
    const filtered = computed(() => {
      const q = query.value.trim().toLowerCase()
      const rows = props.section.rows || []
      if (!q) return rows
      return rows.filter(r => props.section.columns.some(col => cellSearchText(r[col.key]).toLowerCase().includes(q)))
    })
    const visible = computed(() => (expanded.value || query.value ? filtered.value : filtered.value.slice(0, ROWS_COLLAPSED)))

    return () => {
      const s = props.section
      const rows = s.rows || []
      const title = s.title
        ? h(s.level === 3 ? 'h4' : 'h3', { class: s.level === 3 ? 'text-sm font-semibold text-foreground' : 'text-base font-semibold text-foreground' }, s.title)
        : null
      const searchable = rows.length > 15
      const header = h('div', { class: 'flex flex-wrap items-center justify-between gap-2 mb-2' }, [
        h('div', { class: 'flex items-center gap-2' }, [
          title,
          rows.length ? h('span', { class: 'text-xs text-muted-foreground tabular-nums' }, `(${rows.length})`) : null,
        ]),
        searchable
          ? h('label', { class: 'relative' }, [
              h(Search, { class: 'absolute top-1/2 -translate-y-1/2 start-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none' }),
              h('input', {
                value: query.value,
                onInput: e => { query.value = e.target.value },
                type: 'search',
                class: 'h-8 w-56 rounded-md border border-input bg-background ps-8 pe-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring',
              }),
            ])
          : null,
      ])
      if (!rows.length) {
        return h('div', [header, h('div', { class: 'rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground' }, s.empty || '')])
      }
      const thead = h('thead', { class: 'bg-muted/50 sticky top-0 z-10' }, h('tr', s.columns.map(col =>
        h('th', { class: `px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap ${col.num ? 'text-end' : 'text-start'}` }, col.label))))
      const tr = (row, extra = '') => h('tr', { class: extra }, s.columns.map(col =>
        h('td', { class: `px-3 py-2 align-top ${col.num ? 'text-end tabular-nums whitespace-nowrap' : 'text-start'} ${col.reason ? 'max-w-md' : col.num ? '' : 'whitespace-nowrap'}` }, [renderCell(row[col.key], props.dir)])))
      const tbody = h('tbody', { class: 'divide-y divide-border' }, visible.value.map(r => tr(r, 'hover:bg-muted/30')))
      const tfoot = s.footer ? h('tfoot', { class: 'bg-muted/60 font-semibold border-t-2 border-border' }, tr(s.footer)) : null
      const more = !query.value && filtered.value.length > ROWS_COLLAPSED
        ? h('button', {
            class: 'w-full py-2 text-xs font-medium text-primary hover:bg-muted/40 border-t border-border',
            onClick: () => { expanded.value = !expanded.value },
          }, expanded.value ? '−' : `+ ${filtered.value.length - ROWS_COLLAPSED}`)
        : null
      return h('div', [
        header,
        h('div', { class: 'rounded-lg border border-border bg-white overflow-hidden' }, [
          h('div', { class: 'overflow-x-auto max-h-[70vh]' }, h('table', { class: `w-full text-sm ${s.compact ? '[&_td]:py-1.5' : ''}` }, [thead, tbody, tfoot])),
          more,
        ]),
      ])
    }
  },
})

const ReportSection = defineComponent({
  name: 'ReportSection',
  props: { section: Object, dir: String, depth: Number },
  setup(props) {
    const open = ref(false) // per-employee groups start collapsed
    return () => {
      const s = props.section
      switch (s.kind) {
        case 'meta':
          return h('div', { class: 'grid grid-cols-2 md:grid-cols-5 gap-x-6 gap-y-3 rounded-lg border border-border bg-white px-4 py-3' },
            s.items.map(i => h('div', { class: 'min-w-0' }, [
              h('div', { class: 'text-[11px] uppercase tracking-wide text-muted-foreground' }, i.label),
              h('div', { class: 'text-sm font-medium text-foreground truncate', title: i.value }, i.value ?? '–'),
            ])))
        case 'cards':
          return h('div', { class: 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4' }, s.cards.map(c =>
            h('div', { class: `rounded-lg border border-border border-t-4 ${TONE[c.tone] || 'border-t-slate-300'} bg-white px-4 py-3 shadow-sm` }, [
              h('div', { class: 'text-[11px] font-medium uppercase tracking-wide text-muted-foreground' }, c.label),
              h('div', { class: 'mt-1 text-2xl font-bold tabular-nums text-foreground' }, c.value),
              c.sub ? h('div', { class: 'mt-1 text-xs text-muted-foreground leading-snug' }, c.sub) : null,
            ])))
        case 'callout':
          return h('div', { class: 'rounded-lg border border-sky-200 bg-sky-50 px-4 py-2.5 text-xs text-sky-900 leading-relaxed' }, [
            h('span', { innerHTML: s.html }), s.internal ? screenOnlyTag() : null,
          ])
        case 'table':
          return h(ReportTable, { section: s, dir: props.dir, depth: props.depth })
        case 'grid':
          return h('div', { class: 'grid grid-cols-1 lg:grid-cols-2 gap-5' }, s.items.map((x, i) => h(ReportSection, { key: i, section: x, dir: props.dir, depth: props.depth })))
        case 'notes':
          if (!s.items.length) return null
          return h('div', { class: 'rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3' }, [
            s.title ? h('h3', { class: 'text-sm font-semibold text-amber-900 mb-1.5' }, s.title) : null,
            h('ul', { class: 'list-disc ps-5 space-y-1 text-xs text-amber-950 leading-relaxed' }, s.items.map(i => typeof i === 'string'
              ? h('li', { innerHTML: i })
              : h('li', [h('span', { innerHTML: i.html }), i.internal ? screenOnlyTag() : null]))),
          ])
        case 'text':
          return h('p', { class: `text-xs ${s.muted ? 'text-muted-foreground' : 'text-foreground'}` }, s.text)
        case 'group':
          return h('div', { class: 'rounded-lg border border-border bg-muted/20' }, [
            h('button', {
              class: 'w-full flex items-center justify-between gap-3 px-4 py-3 text-start hover:bg-muted/40 rounded-lg',
              onClick: () => { open.value = !open.value },
            }, [
              h('span', { class: 'font-semibold text-foreground' }, s.title),
              h(ChevronDown, { class: `h-4 w-4 text-muted-foreground transition-transform ${open.value ? 'rotate-180' : ''}` }),
            ]),
            open.value ? h('div', { class: 'flex flex-col gap-4 px-4 pb-4' }, s.sections.map((x, i) => h(ReportSection, { key: i, section: x, dir: props.dir, depth: props.depth + 1 }))) : null,
          ])
        default:
          return null
      }
    }
  },
})
</script>
